import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, PaperPlaneRight, ChatCircleDots, Robot } from "@phosphor-icons/react";
import { ChatEngine } from "@/lib/chatEngine";
import { ChatLLM, llmConfigFromEnv } from "@/lib/chatLlm";
import { CHAT_KB } from "@/data/chatKnowledge";
import { submitLead, track, formatApiErrorDetail } from "@/lib/api";

// Confidence thresholds for the rules engine.
const CONFIDENT = 0.42; // answer outright
const MAYBE = 0.22;     // offer "did you mean"

const EMAIL_RE = /^\S+@\S+\.\S+$/;

// Lead capture. `email` is required because the backend's Lead model requires
// a valid address; phone is optional and folded into the details step.
const LEAD_STEPS = [
  { key: "name", prompt: "Happy to get this to the team. What's your name?" },
  {
    key: "email",
    prompt: "What's the best email for them to reply to?",
    validate: (v) => EMAIL_RE.test(v.trim()),
    retry: "That doesn't look like an email address — I need a valid one so the team can actually reach you.",
  },
  { key: "project_type", prompt: "What's the project? (renovation, new build, suite, website, marketing…)" },
  { key: "message", prompt: "Anything else worth knowing — rough size, timing, a phone number, or your area? Type “skip” if not." },
];

/* ------------------------------------------------------------------ */
/* Markdown-lite rendering                                            */
/* Supports **bold**, [text](url) and newlines. Rendered as React      */
/* elements rather than injected HTML, so nothing can smuggle markup   */
/* in through a knowledge-base answer.                                 */
/* ------------------------------------------------------------------ */
function renderInline(text, keyPrefix) {
  const nodes = [];
  const pattern = /\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)]+)\)/g;
  let last = 0;
  let m;
  let i = 0;

  while ((m = pattern.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[1]) {
      nodes.push(<strong key={`${keyPrefix}-b${i}`} className="text-foreground font-semibold">{m[1]}</strong>);
    } else {
      const label = m[2];
      const href = m[3];
      if (href.startsWith("/")) {
        nodes.push(
          <Link key={`${keyPrefix}-l${i}`} to={href} className="text-primary underline underline-offset-2 hover:no-underline">
            {label}
          </Link>,
        );
      } else {
        nodes.push(
          <a
            key={`${keyPrefix}-l${i}`}
            href={href}
            target={href.startsWith("http") ? "_blank" : undefined}
            rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
            className="text-primary underline underline-offset-2 hover:no-underline"
          >
            {label}
          </a>,
        );
      }
    }
    last = pattern.lastIndex;
    i += 1;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

function MessageBody({ text }) {
  return text.split("\n").map((line, i) => (
    <span key={i} className="block min-h-[0.35rem]">
      {renderInline(line, `l${i}`)}
    </span>
  ));
}

/* ------------------------------------------------------------------ */

// `inline` renders the assistant as an always-open panel inside the page
// (the /chat page) instead of the floating dialog.
export function ChatBot({ open: openProp, onClose, inline = false }) {
  const open = inline || openProp;
  const engine = useMemo(() => new ChatEngine(CHAT_KB), []);
  const llm = useMemo(() => new ChatLLM(llmConfigFromEnv()), []);
  const ui = CHAT_KB.ui;

  const [messages, setMessages] = useState([]);
  const [chips, setChips] = useState([]);
  const [typing, setTyping] = useState(false);
  const [input, setInput] = useState("");
  const [lead, setLead] = useState(null);
  const [pending, setPending] = useState(null); // "did you mean" options

  const logRef = useRef(null);
  const inputRef = useRef(null);
  const greeted = useRef(false);
  const timers = useRef([]);

  // Track every timer so an unmount (or a close mid-answer) can't fire a
  // setState on a dead component.
  const later = (fn, ms) => {
    const id = setTimeout(fn, ms);
    timers.current.push(id);
    return id;
  };
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const say = (text) => setMessages((m) => [...m, { role: "assistant", content: text }]);
  const echo = (text) => setMessages((m) => [...m, { role: "user", content: text }]);

  // Greet on first open.
  useEffect(() => {
    if (!open || greeted.current) return;
    greeted.current = true;
    track("chatbot_open");
    setTyping(true);
    later(() => {
      setTyping(false);
      say(ui.greeting);
      setChips(ui.quickReplies);
    }, 500);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the newest message in view, including after chips change the height.
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [messages, chips, typing]);

  useEffect(() => {
    // Inline on /chat: don't pull focus (and the page scroll) on load.
    if (open && !inline) later(() => inputRef.current?.focus(), 60);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Escape closes.
  useEffect(() => {
    if (!open || inline) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, inline, onClose]);

  /* ---------- lead capture ---------- */

  const startLead = (seed = {}) => {
    setLead({ step: 0, data: { ...seed } });
    say(LEAD_STEPS[0].prompt);
  };

  const finishLead = async (data) => {
    setLead(null);
    setTyping(true);
    try {
      await submitLead({
        name: data.name,
        email: data.email,
        phone: "",
        track: "both",
        project_type: data.project_type || "",
        message: data.message || "",
        source: "chatbot",
        company_website: "", // honeypot — must stay empty
      });
      setTyping(false);
      say(`Got it — thanks ${data.name}. Someone from MRE will follow up by email about your **${data.project_type || "project"}**.\n\nIf it's time-sensitive, call **${CHAT_KB.business.phone}**.`);
      setChips(["Ask something else"]);
      track("chatbot_lead", { project_type: data.project_type });
    } catch (err) {
      setTyping(false);
      const detail = formatApiErrorDetail(err?.response?.data?.detail);
      say(`I couldn't submit that just now (${detail}).\n\nPlease call **${CHAT_KB.business.phone}** or use the [contact page](/contact) and it'll get straight through.`);
      setChips(["Try again", "Ask something else"]);
    }
  };

  const stepLead = (text) => {
    const value = text.trim();
    if (/^(cancel|stop|never ?mind|forget it)$/i.test(value)) {
      setLead(null);
      say("No problem — cancelled. Ask me anything else whenever you like.");
      setChips(ui.quickReplies);
      return;
    }

    const step = LEAD_STEPS[lead.step];
    if (step.validate && !step.validate(value)) {
      say(step.retry);
      return;
    }

    const cleaned = step.key === "message" && /^skip$/i.test(value) ? "" : value;
    const data = { ...lead.data, [step.key]: cleaned };
    const nextStep = lead.step + 1;

    if (nextStep < LEAD_STEPS.length) {
      setLead({ step: nextStep, data });
      say(LEAD_STEPS[nextStep].prompt);
    } else {
      finishLead(data);
    }
  };

  /* ---------- answering ---------- */

  const deliver = (result) => {
    const { entry, kind } = result;
    say(entry.answer);
    if (entry.url) say(`More on this: [${entry.name}](${entry.url})`);

    if (entry.cta === "lead" || entry.escalate) setChips(["Leave my details", "Ask something else"]);
    else if (kind === "service" || entry.cta === "estimate") {
      setChips(["Get a free quote", "What areas do you cover?", "Ask something else"]);
    } else setChips(ui.quickReplies);
  };

  const fallback = async (text, ranked) => {
    const gate = llm.check();
    if (!gate.ok) {
      setTyping(false);
      say(ui.fallback);
      setChips(["Leave my details", "What services do you offer?", `Call ${CHAT_KB.business.phone}`]);
      return;
    }
    // LLM path — only reachable once credits are bought and the switch is on.
    try {
      const answer = await llm.ask(text, messages, engine.context(ranked));
      setTyping(false);
      say(answer);
      setChips(["Get a free quote", "Ask something else"]);
      track("chatbot_llm_answer");
    } catch {
      // Any failure — proxy down, no credits, timeout — degrades to the
      // knowledge base rather than showing the visitor an error.
      setTyping(false);
      say(ui.fallback);
      setChips(["Leave my details", "What services do you offer?", `Call ${CHAT_KB.business.phone}`]);
    }
  };

  const respond = async (text) => {
    const lower = text.trim().toLowerCase();

    if (/^call /.test(lower)) {
      setTyping(false);
      say(`Call **${CHAT_KB.business.phone}** — or I can take your details and have someone call you.`);
      setChips(["Leave my details", "Ask something else"]);
      return;
    }
    if (/(quote|estimate|call me|contact me|book|consultation|get started|leave my details|try again)/.test(lower)) {
      setTyping(false);
      startLead();
      return;
    }
    if (/^ask something else$/.test(lower)) {
      setTyping(false);
      say("Sure — what would you like to know?");
      setChips(ui.quickReplies);
      return;
    }

    // Resolve a pending "did you mean" pick before re-ranking.
    if (pending) {
      const picked = pending.find((o) => o.label.toLowerCase() === lower);
      setPending(null);
      if (picked) {
        setTyping(false);
        deliver(picked.ref);
        return;
      }
    }

    const ranked = engine.rank(text);

    if (ranked.confidence >= CONFIDENT) {
      setTyping(false);
      deliver(ranked.top);
      track("chatbot_answer", { entry: ranked.top.entry.id });
      return;
    }

    if (ranked.confidence >= MAYBE) {
      const options = engine.suggestions(ranked, 3);
      if (options.length) {
        setTyping(false);
        setPending(options);
        say("I'm not certain I've got that. Did you mean one of these?");
        setChips(options.map((o) => o.label));
        return;
      }
    }

    await fallback(text, ranked);
  };

  const handle = (text) => {
    if (!text.trim()) return;
    echo(text);
    setChips([]);

    if (lead) {
      stepLead(text);
      return;
    }
    setTyping(true);
    later(() => { respond(text); }, 320);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const text = input;
    setInput("");
    handle(text);
  };

  /* ---------- render ---------- */

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 16, scale: 0.98 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
          className={inline
            ? "relative bg-card border border-border flex flex-col h-[70vh] min-h-[560px]"
            : `fixed z-50 bg-card border border-border shadow-2xl flex flex-col
                     inset-0 sm:inset-auto sm:bottom-4 sm:right-4 sm:w-[380px] sm:h-[560px] sm:max-h-[calc(100vh-2rem)]`}
          role={inline ? "region" : "dialog"}
          aria-label="MRE assistant"
          data-testid="chatbot-panel"
        >
          {/* header */}
          <header className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border bg-background">
            <div className="flex items-center gap-3 min-w-0">
              <span className="grid place-items-center w-9 h-9 bg-primary text-primary-foreground shrink-0">
                <Robot size={20} weight="fill" />
              </span>
              <span className="min-w-0">
                <strong className="block font-display uppercase tracking-tight text-sm leading-tight">{ui.title}</strong>
                <small className="block text-muted-foreground text-xs truncate">{ui.subtitle}</small>
              </span>
            </div>
            {!inline && <button
              onClick={onClose}
              aria-label="Close chat"
              data-testid="chatbot-close"
              className="grid place-items-center w-8 h-8 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X size={20} />
            </button>}
          </header>

          {/* log */}
          <div
            ref={logRef}
            className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-2.5"
            role="log"
            aria-live="polite"
            data-testid="chatbot-log"
          >
            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <div
                  data-testid={m.role === "user" ? "chatbot-msg-user" : "chatbot-msg-bot"}
                  className={`max-w-[86%] px-3.5 py-2.5 text-sm leading-relaxed break-words ${
                    m.role === "user"
                      ? "bg-primary text-primary-foreground"
                      : "bg-background border border-border text-muted-foreground"
                  }`}
                >
                  <MessageBody text={m.content} />
                </div>
              </div>
            ))}

            {typing && (
              <div className="flex justify-start" data-testid="chatbot-typing">
                <div className="bg-background border border-border px-4 py-3.5 flex gap-1.5">
                  {[0, 1, 2].map((i) => (
                    <motion.span
                      key={i}
                      className="w-1.5 h-1.5 rounded-full bg-muted-foreground"
                      animate={{ opacity: [0.25, 1, 0.25], y: [0, -3, 0] }}
                      transition={{ duration: 1.1, repeat: Infinity, delay: i * 0.16 }}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* quick replies */}
          {chips.length > 0 && (
            <div className="flex flex-wrap gap-1.5 px-4 pb-1">
              {chips.map((c) => (
                <button
                  key={c}
                  onClick={() => handle(c)}
                  data-testid="chatbot-chip"
                  className="border border-primary/50 text-primary font-mono-tech text-[11px] tracking-widest uppercase px-3 py-1.5 hover:bg-primary hover:text-primary-foreground transition-colors"
                >
                  {c}
                </button>
              ))}
            </div>
          )}

          {/* composer */}
          <form onSubmit={onSubmit} className="flex items-center gap-2 px-4 py-3">
            <label htmlFor="chatbot-input" className="sr-only">Your message</label>
            <input
              id="chatbot-input"
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={ui.placeholder}
              maxLength={1000}
              autoComplete="off"
              data-testid="chatbot-input"
              className="flex-1 min-w-0 bg-background border border-border px-3.5 h-11 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
            />
            <button
              type="submit"
              aria-label="Send message"
              data-testid="chatbot-send"
              className="grid place-items-center w-11 h-11 bg-primary text-primary-foreground hover:bg-[hsl(16,100%,55%)] transition-colors shrink-0"
            >
              <PaperPlaneRight size={18} weight="fill" />
            </button>
          </form>

          <p className="px-4 pb-3 text-[11px] leading-snug text-muted-foreground">
            Answers come from MRE's own service information. For anything urgent, call {CHAT_KB.business.phone}.
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export { ChatCircleDots };
export default ChatBot;
